import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';

import { formatDateTime, formatFileSize } from './format';
import type { FileBrowserItem } from './types';

interface FileBrowserTableProps {
  items: FileBrowserItem[];
  /** Undefined at the root — the two fixed entries can't be renamed/deleted/downloaded. */
  entry?: string;
  onNavigateInto: (item: FileBrowserItem) => void;
  onDownload: (items: Extract<FileBrowserItem, { type: 'file' }>[]) => void;
  onRename: (item: FileBrowserItem) => void;
  onMove: (items: FileBrowserItem[]) => void;
  /** Drag-and-drop of a single file directly onto a folder row — moves it there with no picker. */
  onMoveInto: (item: Extract<FileBrowserItem, { type: 'file' }>, folder: Extract<FileBrowserItem, { type: 'folder' }>) => void;
  onDelete: (items: FileBrowserItem[]) => void;
}

const itemKey = (item: FileBrowserItem): string => `${item.type}:${item.name}`;

type SortField = 'name' | 'lastModified';
type SortOrder = 'asc' | 'desc';

/**
 * Folders always group before files; within each group, sort by the active
 * field/order. Folders lack `lastModified`, so a date sort leaves them in fetched order.
 */
const compareItems = (a: FileBrowserItem, b: FileBrowserItem, sortField: SortField, sortOrder: SortOrder): number => {
  if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;

  let result = 0;
  if (sortField === 'name') {
    result = a.name.localeCompare(b.name);
  } else if (a.type === 'file' && b.type === 'file') {
    result = (a.lastModified ? new Date(a.lastModified).getTime() : 0) -
      (b.lastModified ? new Date(b.lastModified).getTime() : 0);
  }
  return sortOrder === 'asc' ? result : -result;
};

const FileBrowserTable = ({ items, entry, onNavigateInto, onDownload, onRename, onMove, onMoveInto, onDelete }: FileBrowserTableProps) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement, item: FileBrowserItem } | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [draggedItem, setDraggedItem] = useState<Extract<FileBrowserItem, { type: 'file' }> | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  const canManage = Boolean(entry);
  const allSelected = items.length > 0 && selected.size === items.length;
  const selectedItems = items.filter(item => selected.has(itemKey(item)));
  const selectedFiles = selectedItems.filter((item): item is Extract<FileBrowserItem, { type: 'file' }> => item.type === 'file');
  const sortedItems = [...items].sort((a, b) => compareItems(a, b, sortField, sortOrder));

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map(itemKey)));
  };

  const toggleOne = (item: FileBrowserItem) => {
    const key = itemKey(item);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const closeMenu = () => setMenuAnchor(null);

  const handleDrop = (folder: Extract<FileBrowserItem, { type: 'folder' }>) => {
    if (draggedItem) onMoveInto(draggedItem, folder);
    setDraggedItem(null);
    setDragOverKey(null);
  };

  return (
    <Box>
      {canManage && selected.size > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {selected.size}
            {' '}
            selected
          </Typography>
          <Tooltip title={selectedFiles.length === 0 ? 'Select at least one file to download' : ''}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={selectedFiles.length === 0}
                onClick={() => onDownload(selectedFiles)}
              >
                Download
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DriveFileMoveIcon />}
            onClick={() => onMove(selectedItems)}
          >
            Move
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => onDelete(selectedItems)}
          >
            Delete
          </Button>
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            {canManage && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={selected.size > 0 && !allSelected}
                  onChange={toggleAll}
                />
              </TableCell>
            )}
            <TableCell sx={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary' }}>
              <TableSortLabel
                active={sortField === 'name'}
                direction={sortField === 'name' ? sortOrder : 'asc'}
                onClick={() => toggleSort('name')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary' }}>Size</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary' }}>
              <TableSortLabel
                active={sortField === 'lastModified'}
                direction={sortField === 'lastModified' ? sortOrder : 'asc'}
                onClick={() => toggleSort('lastModified')}
              >
                Uploaded
              </TableSortLabel>
            </TableCell>
            {canManage && (
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary' }}>Actions</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={canManage ? 5 : 3}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  This folder is empty.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {sortedItems.map((item) => {
            const key = itemKey(item);
            const isFolder = item.type === 'folder';
            const isDropTarget = canManage && isFolder && Boolean(draggedItem);
            return (
              <TableRow
                key={key}
                hover
                draggable={canManage && !isFolder}
                onDragStart={canManage && !isFolder ? () => setDraggedItem(item) : undefined}
                onDragEnd={canManage && !isFolder ? () => { setDraggedItem(null); setDragOverKey(null); } : undefined}
                onDragOver={isDropTarget ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                onDragEnter={isDropTarget ? () => setDragOverKey(key) : undefined}
                onDragLeave={isDropTarget ? () => setDragOverKey(prev => (prev === key ? null : prev)) : undefined}
                onDrop={isDropTarget ? (e) => { e.preventDefault(); handleDrop(item); } : undefined}
                sx={theme => ({
                  opacity: draggedItem && itemKey(draggedItem) === key ? 0.4 : 1,
                  cursor: canManage && !isFolder ? 'grab' : undefined,
                  ...(dragOverKey === key && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    outline: `2px dashed ${theme.palette.primary.main}`,
                    outlineOffset: '-2px',
                  }),
                })}
              >
                {canManage && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.has(key)} onChange={() => toggleOne(item)} />
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isFolder ?
                      <FolderIcon sx={{ color: 'primary.main', fontSize: 20 }} /> :
                      <InsertDriveFileIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
                    {isFolder ?
                      (
                        <Link component="button" underline="hover" sx={{ fontWeight: 600, fontSize: 14 }} onClick={() => onNavigateInto(item)}>
                          {item.name}
                        </Link>
                      ) :
                        <Typography sx={{ fontSize: 14 }}>{item.name}</Typography>}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {isFolder ? '—' : formatFileSize(item.size)}
                </TableCell>
                <TableCell>
                  {isFolder ? '—' : formatDateTime(item.lastModified)}
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    {isFolder ? null : (
                      <Tooltip title="Download">
                        <IconButton size="small" color="primary" onClick={() => onDownload([item])}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" onClick={e => setMenuAnchor({ el: e.currentTarget, item })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { if (menuAnchor) onRename(menuAnchor.item); closeMenu(); }}>
          Rename
        </MenuItem>
        <MenuItem onClick={() => { if (menuAnchor) onMove([menuAnchor.item]); closeMenu(); }}>
          Move to…
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => { if (menuAnchor) onDelete([menuAnchor.item]); closeMenu(); }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FileBrowserTable;
